import { useEffect, useRef, type ReactNode } from "react";
import "./mailcal.css";

export type FullScreenPanelProps = {
  accessibilityLabel: string;
  /** The top bar (close, title, the main action). */
  header: ReactNode;
  /** The bottom bar: stays right above the on-screen keyboard. */
  footer: ReactNode;
  testID: string;
  children: ReactNode;
};

/**
 * A task that takes the whole phone screen (a reply): a top bar, one scrolling area and a bottom bar. It follows the
 * visual viewport, which is what shrinks when the keyboard opens. Web only; iOS / Android use their own full screens.
 */
export function FullScreenPanel({ accessibilityLabel, header, footer, testID, children }: FullScreenPanelProps) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (vv === null) return undefined;
    const fit = () => {
      if (ref.current === null) return;
      ref.current.style.height = `${vv.height}px`;
      ref.current.style.top = `${vv.offsetTop}px`;
    };
    fit();
    vv.addEventListener("resize", fit);
    vv.addEventListener("scroll", fit);
    return () => {
      vv.removeEventListener("resize", fit);
      vv.removeEventListener("scroll", fit);
    };
  }, []);
  return (
    <section ref={ref} className="b-fullscreen" role="dialog" aria-modal="true" aria-label={accessibilityLabel} data-testid={testID}>
      <header className="b-fullscreen-head">{header}</header>
      <div className="b-fullscreen-body">{children}</div>
      <footer className="b-fullscreen-foot">{footer}</footer>
    </section>
  );
}
